<template>
  <div class="h-iw">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-iw__content">
      <!-- ===== Page head ===== -->
      <div class="h-iw__head">
        <h1 class="h-iw__title">Connect a consuming product</h1>
        <p class="h-iw__sub">
          Wire a self-hosted product into Hermes so licence, usage and billing events sync in real time.
          <template v-if="product"> You're connecting <b>{{ product.name }}</b>.</template>
        </p>
      </div>

      <!-- ===== Horizontal stepper ===== -->
      <HermesWizardStepper :steps="topSteps" :current="currentStep" @step-click="goToStep" />

      <div class="h-iw__grid">
        <!-- ===== LEFT: progress checklist with artifacts ===== -->
        <div class="h-card">
          <div class="h-card__head">
            <h3>Progress</h3>
            <span class="h-badge h-badge--info"><span class="h-dot" style="background:var(--h-info)"></span>{{ doneCount }} / {{ steps.length }}</span>
          </div>
          <div class="h-vsteps">
            <div
              v-for="(s, i) in steps"
              :key="s.key"
              class="h-vstep"
              :class="vstepClass(i)"
            >
              <div class="h-vnode">
                <svg v-if="isStepDone(i)" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <div>
                <div class="h-vstep__t">{{ s.checkTitle }}</div>
                <div class="h-vstep__d" v-html="artifact(s.key)"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== RIGHT: active step pane ===== -->
        <div class="h-card">
          <div class="h-wcard__head">
            <h3>Step {{ currentStep + 1 }} · {{ steps[currentStep].title }}</h3>
            <p>{{ steps[currentStep].blurb }}</p>
          </div>

          <div class="h-wcard__body">
            <!-- ── Step 1: Register ── -->
            <div v-if="currentStep === 0">
              <FormKit type="form" :actions="false" @submit="doRegister" v-model="reg">
                <FormKit type="text" name="name" label="Product name" validation="required"
                  placeholder="e.g. Musubi" help="The human name shown across the admin + portal." />
                <FormKit type="text" name="slug" label="Slug"
                  :placeholder="slugPlaceholder" help="Stable machine id used in licence keys + URLs. Leave blank to derive from the name." />
                <FormKit type="checkbox" name="requires_activation"
                  label="Licenced product (issues signed-JWT keys + expects heartbeats)" />
                <FormKit type="text" name="usage_metrics_raw" label="Usage metrics (optional)"
                  placeholder="servers, tenants, api.calls"
                  help="Comma-separated metric keys the product reports for tier enforcement." />
                <p v-if="regError" class="h-formerror">{{ regError }}</p>
                <button type="submit" hidden ref="regSubmit"></button>
              </FormKit>
            </div>

            <!-- ── Step 2: API key ── -->
            <div v-else-if="currentStep === 1">
              <HermesApiKeyRevealField
                v-if="apiKeyPlaintext"
                label="Integration API key"
                :value="apiKeyPlaintext"
                :start-revealed="true"
                warning="Copy this now — it is shown once and never again. Store it in the product's Hermes config as X-Hermes-Api-Key / Authorization: Bearer."
              />
              <div v-else class="h-note">The API key was issued during registration. Re-run the wizard to mint a fresh one.</div>
              <div class="h-kv">
                <span class="h-kv__k">Product</span>
                <span class="h-kv__v">{{ product ? product.name : '—' }} <span class="h-mono">{{ product ? product.slug : '' }}</span></span>
              </div>
            </div>

            <!-- ── Step 3: Test connection ── -->
            <div v-else-if="currentStep === 2">
              <p class="h-steplede">Call the handshake endpoint from your product with the key above. Hermes stamps the key the moment it arrives.</p>
              <div class="h-codeblock">
                <button class="h-copybtn" @click="copyText(curlSnippet)">{{ copiedSnippet ? 'Copied' : 'Copy' }}</button>
                <pre>{{ curlSnippet }}</pre>
              </div>

              <!-- handshake live check -->
              <div v-if="handshake" class="h-livecheck">
                <div class="h-lc-ic"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg></div>
                <div>
                  <div class="h-lc-t">Handshake succeeded</div>
                  <div class="h-lc-d">GET /v1/ping → 200 OK · key <span class="h-mono">{{ keyPrefixShort }}</span></div>
                </div>
              </div>
              <div v-else class="h-waiting">
                <span class="h-spin"></span>
                <div>
                  <div class="h-waiting__t">Awaiting handshake</div>
                  <div class="h-waiting__d">Listening for the install to call <span class="h-mono">/api/v1/ping</span>…</div>
                </div>
              </div>

              <!-- heartbeat live check (licenced products) -->
              <div v-if="product && product.requires_activation" class="h-waiting" :class="{ 'h-waiting--ok': heartbeat }" style="margin-top:10px">
                <span v-if="!heartbeat" class="h-spin"></span>
                <div v-else class="h-lc-ic"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg></div>
                <div>
                  <div class="h-waiting__t">{{ heartbeat ? 'First heartbeat received' : 'Awaiting first heartbeat' }}</div>
                  <div class="h-waiting__d">Listening for the install to call <span class="h-mono">/api/v1/activations/refresh</span>…</div>
                </div>
              </div>
            </div>

            <!-- ── Step 4: Webhooks ── -->
            <div v-else-if="currentStep === 3">
              <template v-if="!webhook.endpoint">
                <div class="h-field">
                  <label class="h-flabel">Endpoint URL</label>
                  <input class="h-input h-mono" v-model="webhook.url" placeholder="https://api.your-product.app/hermes/webhook" />
                </div>
                <div class="h-field">
                  <label class="h-flabel">Event families</label>
                  <input class="h-input h-mono" v-model="webhook.events" placeholder="subscription.*, licence.*" />
                  <p class="h-fhint">Comma-separated. Hermes signs every delivery with an HMAC-SHA256 <span class="h-mono">X-Hermes-Signature</span> header.</p>
                </div>
                <p v-if="webhook.error" class="h-formerror">{{ webhook.error }}</p>
                <button class="h-btn h-btn--primary" :disabled="webhook.creating" @click="createWebhook">
                  {{ webhook.creating ? 'Creating…' : 'Create endpoint' }}
                </button>
              </template>

              <template v-else>
                <div class="h-badge-row">
                  <span class="h-badge h-badge--success"><span class="h-dot" style="background:var(--h-success)"></span>endpoint created</span>
                  <span class="h-mono h-muted">{{ webhook.endpoint.url }}</span>
                </div>

                <div class="h-field" style="margin-top:14px" v-if="webhook.secret">
                  <label class="h-flabel">Signing secret <span class="h-muted">· copy into your config</span></label>
                  <div class="h-codeblock">
                    <button class="h-copybtn" @click="copyText(webhookConfigSnippet)">{{ copiedWebhook ? 'Copied' : 'Copy' }}</button>
                    <pre v-html="webhookConfigHtml"></pre>
                  </div>
                  <p class="h-fhint">Shown once. Verify each delivery: <span class="h-mono">HMAC-SHA256(secret, t + "." + body)</span> equals the <span class="h-mono">v1</span> in the header.</p>
                </div>

                <div class="h-tc-row">
                  <span v-if="webhook.test && webhook.test.ok" class="h-tc-result">
                    <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    Test <span class="h-mono">ping</span> delivered · {{ webhook.test.status_code }} · {{ webhook.test.latency_ms }} ms
                  </span>
                  <span v-else-if="webhook.test" class="h-tc-fail">
                    Delivery failed · status {{ webhook.test.status_code }}{{ webhook.test.error ? ' · ' + webhook.test.error : '' }}
                  </span>
                  <span v-else class="h-muted">Send a signed <span class="h-mono">ping</span> to verify the endpoint receives + validates it.</span>
                  <button class="h-btn h-btn--secondary h-btn--sm" :disabled="webhook.testing" @click="testFireWebhook">
                    {{ webhook.testing ? 'Sending…' : (webhook.test ? 'Re-test' : 'Test fire') }}
                  </button>
                </div>
              </template>
            </div>

            <!-- ── Step 5: Gateways ── -->
            <div v-else-if="currentStep === 4">
              <p class="h-steplede">Validate the payment gateways this product will sell through. Each runs a cheap authenticated check — Hermes never stores card data.</p>
              <div v-for="gw in gatewayList" :key="gw.key" class="h-gwsec">
                <div class="h-gwsec__head">
                  <div class="h-gwsec__logo" :style="{ background: gw.color }">{{ gw.glyph }}</div>
                  <div style="flex:1">
                    <div class="h-gwsec__name">
                      {{ gw.label }}
                      <span v-if="gw.key === 'create_pay'" class="h-badge h-badge--neutral" style="margin-left:6px">kickback</span>
                    </div>
                    <div class="h-gwsec__meta">{{ gw.meta }}</div>
                  </div>
                  <span class="h-badge" :class="gwBadgeClass(gateways[gw.key].status)">
                    <span class="h-dot" :style="{ background: gwDotColor(gateways[gw.key].status) }"></span>{{ gwStatusLabel(gateways[gw.key].status) }}
                  </span>
                </div>
                <div class="h-gwsec__body">
                  <div class="h-tc-row" style="border-top:none; margin-top:0; padding-top:0">
                    <span v-if="gateways[gw.key].reason" class="h-muted" style="font-size:12.5px">{{ gateways[gw.key].reason }}</span>
                    <span v-else class="h-muted" style="font-size:12.5px">Run an authenticated check to verify credentials.</span>
                    <button class="h-btn h-btn--secondary h-btn--sm" :disabled="gateways[gw.key].busy" @click="validateGateway(gw.key)">
                      {{ gateways[gw.key].busy ? 'Checking…' : (gateways[gw.key].status ? 'Re-check' : 'Validate') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Step 6: Go live ── -->
            <div v-else-if="currentStep === 5">
              <div class="h-health__grid">
                <div class="h-hcell">
                  <div class="h-hl">Product</div>
                  <div class="h-hv"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>Registered</div>
                  <div class="h-hs">slug {{ product ? product.slug : '—' }}</div>
                </div>
                <div class="h-hcell">
                  <div class="h-hl">API key</div>
                  <div class="h-hv"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>Active</div>
                  <div class="h-hs">{{ keyPrefixShort }}…</div>
                </div>
                <div class="h-hcell">
                  <div class="h-hl">Connection</div>
                  <div class="h-hv" :class="{ 'h-hv--warn': !handshake }">
                    <svg v-if="handshake" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    <svg v-else viewBox="0 0 24 24" class="h-warnic"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>
                    {{ handshake ? 'Handshake OK' : 'Not tested' }}
                  </div>
                  <div class="h-hs">{{ handshake ? '/ping received' : 'no handshake yet' }}</div>
                </div>
                <div class="h-hcell">
                  <div class="h-hl">Webhooks</div>
                  <div class="h-hv" :class="{ 'h-hv--warn': !webhookVerified }">
                    <svg v-if="webhookVerified" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    <svg v-else viewBox="0 0 24 24" class="h-warnic"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>
                    {{ webhookVerified ? 'Delivering' : 'Skipped' }}
                  </div>
                  <div class="h-hs">{{ webhook.endpoint ? '1 endpoint' : 'none configured' }}</div>
                </div>
                <div class="h-hcell">
                  <div class="h-hl">Gateways</div>
                  <div class="h-hv" :class="{ 'h-hv--warn': gatewaysConnected === 0 }">
                    <svg v-if="gatewaysConnected > 0" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    <svg v-else viewBox="0 0 24 24" class="h-warnic"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>
                    {{ gatewaysConnected }} of {{ gatewayList.length }}
                  </div>
                  <div class="h-hs">{{ gatewaySummary }}</div>
                </div>
              </div>

              <div v-if="goLiveWarnings.length" class="h-golive-warn">
                <svg viewBox="0 0 24 24"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>
                <div>
                  <div class="h-golive-warn__t">You can go live, but some steps are unverified</div>
                  <ul><li v-for="w in goLiveWarnings" :key="w">{{ w }}</li></ul>
                </div>
              </div>
              <div v-else class="h-golive-ok">
                <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                Everything checks out — {{ product ? product.name : 'the product' }} is ready to go live.
              </div>
            </div>
          </div>

          <!-- ── Footer ── -->
          <div class="h-wcard__foot">
            <span class="h-wprogress">Step {{ currentStep + 1 }} of {{ steps.length }} · {{ Math.round(((currentStep + 1) / steps.length) * 100) }}% complete</span>
            <div class="h-wcard__foot-btns">
              <button v-if="currentStep > 0" class="h-btn h-btn--secondary" @click="back">Back</button>
              <button v-if="steps[currentStep].skippable && currentStep < steps.length - 1" class="h-btn h-btn--ghost" @click="skip">Skip for now</button>
              <button
                v-if="currentStep < steps.length - 1"
                class="h-btn h-btn--primary"
                :disabled="primaryDisabled"
                @click="next"
              >{{ primaryLabel }}</button>
              <button v-else class="h-btn h-btn--primary" @click="finish">Go live</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Toasts ===== -->
    <div class="h-toasts" aria-live="polite">
      <div v-for="t in toasts" :key="t.id" class="h-toast" :class="`h-toast--${t.kind}`">{{ t.text }}</div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesIntegrationWizard — Phase 4 island (admin-integration-wizard.html).
// A 6-step wizard (Register → API key → Test connection → Webhooks → Gateways →
// Go live) driving the Phase 4 integration + webhooks APIs. Left column is a
// live checklist recording each step's artifact; right column is the active step.
// Webhooks + Gateways are skippable; skips are reflected in the go-live summary.
export default {
  name: 'HermesIntegrationWizard',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      apiBase: this.componentData?.apiBase || '/api/v1',
      vendorName: this.componentData?.vendorName || '',
      origin: (typeof window !== 'undefined' && window.location) ? window.location.origin : 'https://hermes.ddev.site',

      currentStep: 0,

      // Step definitions (right pane titles + left checklist meta).
      steps: [
        { key: 'register', title: 'Register the product', checkTitle: 'Product registered', blurb: 'Name the consuming product and choose whether it needs signed licence keys.', skippable: false },
        { key: 'apikey', title: 'API key', checkTitle: 'API key issued', blurb: 'Copy the integration key — it authenticates every call your product makes to Hermes. Shown once.', skippable: false },
        { key: 'connection', title: 'Test connection', checkTitle: 'Connection tested', blurb: 'Call the handshake endpoint from your product; Hermes confirms the moment it lands.', skippable: false },
        { key: 'webhooks', title: 'Webhooks', checkTitle: 'Webhooks verified', blurb: 'Register a signed webhook endpoint and fire a test event. Optional — you can add this later.', skippable: true },
        { key: 'gateways', title: 'Gateway credentials', checkTitle: 'Gateway credentials', blurb: 'Validate the payment gateways the product will sell through. Optional — connect these when ready.', skippable: true },
        { key: 'golive', title: 'Go live', checkTitle: 'Go live', blurb: 'Review the integration-health summary and confirm.', skippable: false },
      ],

      // Register.
      reg: { name: '', slug: '', requires_activation: true, usage_metrics_raw: '' },
      regError: '',

      // Registration result.
      product: null,
      apiKeyPlaintext: '',
      apiKeyMasked: '',
      keyId: null,

      // Connection.
      handshake: false,
      heartbeat: false,
      sinceIso: '',
      pollTimer: null,
      copiedSnippet: false,

      // Webhooks.
      webhook: { url: '', events: 'subscription.*, licence.*', endpoint: null, secret: '', creating: false, testing: false, test: null, error: '', skipped: false },
      copiedWebhook: false,

      // Gateways.
      gateways: {
        stripe:     { status: null, reason: null, busy: false },
        revolut:    { status: null, reason: null, busy: false },
        create_pay: { status: null, reason: null, busy: false, kickback_note: '' },
      },
      gatewaysSkipped: false,

      toasts: [],
      toastSeq: 0,

      crumbs: [{ label: 'Integrations' }, { label: 'Connect a product' }],
    };
  },
  computed: {
    topSteps() {
      return this.steps.map((s, i) => ({
        key: s.key,
        label: this.shortLabel(s.key),
        hint: this.stepHint(i),
        done: this.isStepDone(i),
      }));
    },
    doneCount() {
      return this.steps.reduce((n, _s, i) => n + (this.isStepDone(i) ? 1 : 0), 0);
    },
    slugPlaceholder() {
      return this.reg.name ? this.slugify(this.reg.name) : 'my-product';
    },
    keyPrefixShort() {
      if (this.apiKeyMasked) return this.apiKeyMasked.replace(/\*+$/, '');
      if (this.apiKeyPlaintext) return this.apiKeyPlaintext.slice(0, 16);
      return '';
    },
    curlSnippet() {
      const key = this.apiKeyPlaintext || 'hk_live_…';
      return `curl ${this.origin}/api/v1/ping \\\n  -H "Authorization: Bearer ${key}"`;
    },
    webhookConfigSnippet() {
      const events = this.webhookEventsList.map((e) => `"${e}"`).join(', ');
      return `# hermes.yml\nsigning_secret: "${this.webhook.secret}"\nevents: [${events}]`;
    },
    webhookConfigHtml() {
      const events = this.webhookEventsList.map((e) => `<span class="s">"${e}"</span>`).join(', ');
      return `<span class="c"># hermes.yml</span>\n<span class="k">signing_secret</span>: <span class="s">"${this.esc(this.webhook.secret)}"</span>\n<span class="k">events</span>: [${events}]`;
    },
    webhookEventsList() {
      const raw = (this.webhook.endpoint && this.webhook.endpoint.events && this.webhook.endpoint.events.length)
        ? this.webhook.endpoint.events
        : String(this.webhook.events || '').split(',');
      return raw.map((e) => String(e).trim()).filter(Boolean);
    },
    webhookVerified() {
      return !!(this.webhook.test && this.webhook.test.ok);
    },
    gatewayList() {
      return [
        { key: 'stripe', label: 'Stripe', glyph: 'S', color: '#635BFF', meta: 'Cards · SEPA · recurring' },
        { key: 'revolut', label: 'Revolut Business', glyph: 'R', color: '#191C1F', meta: 'Cards · Revolut Pay' },
        { key: 'create_pay', label: 'Create Pay', glyph: 'C', color: '#0EA5E9', meta: 'Apexl network gateway · revenue-share' },
      ];
    },
    gatewaysConnected() {
      return this.gatewayList.reduce((n, gw) => n + (this.gateways[gw.key].status === 'connected' ? 1 : 0), 0);
    },
    gatewaySummary() {
      const failed = this.gatewayList.filter((gw) => this.gateways[gw.key].status === 'failed');
      if (failed.length) return `${failed.map((g) => g.label).join(', ')} failed`;
      const unchecked = this.gatewayList.filter((gw) => !this.gateways[gw.key].status);
      if (unchecked.length === this.gatewayList.length) return 'none checked yet';
      return `${this.gatewaysConnected} connected`;
    },
    goLiveWarnings() {
      const w = [];
      if (!this.handshake) w.push('The connection handshake has not been observed yet — your product has not called /ping.');
      if (!this.webhookVerified) w.push('No webhook delivery has been verified — lifecycle events will not reach the product.');
      if (this.gatewaysConnected === 0) w.push('No payment gateway is connected — the product cannot take payment.');
      return w;
    },
    canAdvance() {
      // Register + API-key + connection are hard gates; webhooks/gateways are skippable.
      if (this.currentStep === 0) return !!this.product; // advance only once registered
      if (this.currentStep === 1) return !!this.apiKeyPlaintext;
      if (this.currentStep === 2) return this.handshake;
      return true;
    },
    // On the first step, before a product exists, the primary button SUBMITS the
    // registration form (next() → submitRegister). Gating it on canAdvance, which
    // is false until a product exists, made "Register product" unclickable: the
    // form could only be sent by pressing Enter in a field.
    primaryDisabled() {
      if (this.currentStep === 0 && !this.product) return false;
      return !this.canAdvance;
    },
    primaryLabel() {
      if (this.currentStep === 0) return this.product ? 'Continue' : 'Register product';
      return 'Continue';
    },
  },
  methods: {
    // ── Step navigation ──
    isStepDone(i) {
      switch (this.steps[i].key) {
        case 'register': return !!this.product;
        case 'apikey': return !!this.apiKeyPlaintext && this.currentStep > 1;
        case 'connection': return this.handshake;
        case 'webhooks': return this.webhookVerified || this.webhook.skipped;
        case 'gateways': return this.gatewaysConnected > 0 || this.gatewaysSkipped;
        case 'golive': return false;
        default: return false;
      }
    },
    vstepClass(i) {
      if (this.isStepDone(i)) return 'is-done';
      if (i === this.currentStep) return 'is-active';
      return 'is-pending';
    },
    shortLabel(key) {
      return {
        register: 'Register', apikey: 'API key', connection: 'Test connection',
        webhooks: 'Webhooks', gateways: 'Gateways', golive: 'Go live',
      }[key] || key;
    },
    stepHint(i) {
      const key = this.steps[i].key;
      if (key === 'register') return this.product ? this.product.slug : 'Product';
      if (key === 'apikey') return this.apiKeyPlaintext ? 'Issued' : 'Key';
      if (key === 'connection') return this.handshake ? 'Passed' : 'Handshake';
      if (key === 'webhooks') return this.webhookVerified ? 'Verified' : (this.webhook.skipped ? 'Skipped' : 'Optional');
      if (key === 'gateways') return this.gatewaysConnected > 0 ? `${this.gatewaysConnected} live` : (this.gatewaysSkipped ? 'Skipped' : 'Optional');
      return 'Confirm';
    },
    artifact(key) {
      switch (key) {
        case 'register':
          return this.product
            ? `slug <span class="mono">${this.esc(this.product.slug)}</span>${this.product.requires_activation ? ' · requires activation' : ' · one-off'}`
            : 'Name + type the consuming product';
        case 'apikey':
          return this.apiKeyPlaintext
            ? `<span class="mono">${this.esc(this.keyPrefixShort)}…</span> · revealed once`
            : 'Mint the integration key';
        case 'connection':
          return this.handshake ? 'Handshake 200 OK' : 'Awaiting /ping handshake';
        case 'webhooks':
          if (this.webhookVerified) return `Test <span class="mono">ping</span> delivered · ${this.webhook.test.status_code}`;
          if (this.webhook.endpoint) return 'Endpoint created — test pending';
          if (this.webhook.skipped) return 'Skipped for now';
          return 'Register a signed endpoint';
        case 'gateways':
          if (this.gatewaysConnected > 0) return `${this.gatewaysConnected} of ${this.gatewayList.length} connected`;
          if (this.gatewaysSkipped) return 'Skipped for now';
          return 'Validate Stripe, Revolut &amp; Create Pay';
        case 'golive':
          return 'Review the integration-health summary';
        default: return '';
      }
    },
    goToStep(i) {
      if (i <= this.currentStep || this.isStepDone(i)) {
        this.leaveStep();
        this.currentStep = i;
        this.enterStep();
      }
    },
    back() {
      if (this.currentStep === 0) return;
      this.leaveStep();
      this.currentStep -= 1;
      this.enterStep();
    },
    next() {
      if (this.currentStep === 0 && !this.product) { this.submitRegister(); return; }
      if (!this.canAdvance) return;
      this.advance();
    },
    skip() {
      if (this.steps[this.currentStep].key === 'webhooks') this.webhook.skipped = true;
      if (this.steps[this.currentStep].key === 'gateways') this.gatewaysSkipped = true;
      this.advance();
    },
    advance() {
      if (this.currentStep >= this.steps.length - 1) return;
      this.leaveStep();
      this.currentStep += 1;
      this.enterStep();
    },
    enterStep() {
      if (this.steps[this.currentStep].key === 'connection') this.startPolling();
    },
    leaveStep() {
      this.stopPolling();
    },
    finish() {
      this.toast(`${this.product ? this.product.name : 'Product'} is live 🎉`, 'success');
    },

    // ── Step 1: register ──
    submitRegister() {
      if (this.$refs.regSubmit) this.$refs.regSubmit.click();
    },
    async doRegister() {
      this.regError = '';
      const name = String(this.reg.name || '').trim();
      if (!name) { this.regError = 'A product name is required.'; return; }
      const payload = {
        name,
        slug: String(this.reg.slug || '').trim() || this.slugify(name),
        requires_activation: !!this.reg.requires_activation,
      };
      const metrics = String(this.reg.usage_metrics_raw || '').split(',').map((m) => m.trim()).filter(Boolean);
      if (metrics.length) {
        payload.usage_metrics = {};
        metrics.forEach((m) => { payload.usage_metrics[m] = 'count'; });
      }
      try {
        const data = await this.api.post(`${this.apiBase}/admin/integration/register`, payload);
        this.product = data.product;
        this.apiKeyPlaintext = data.api_key_plaintext;
        this.apiKeyMasked = data.api_key ? data.api_key.masked : '';
        this.keyId = data.api_key ? data.api_key.id : null;
        this.webhook.url = `${this.origin}/hermes/webhook`;
        this.toast('Product registered · key issued', 'success');
        this.advance();
      } catch (e) {
        this.regError = this.serverMsg(e) || 'Registration failed. Please try again.';
      }
    },

    // ── Step 3: connection polling ──
    startPolling() {
      this.stopPolling();
      if (!this.sinceIso) this.sinceIso = new Date(Date.now() - 5000).toISOString();
      this.pollHandshake();
      this.pollTimer = setInterval(() => this.pollHandshake(), 3000);
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
    async pollHandshake() {
      if (!this.keyId) return;
      try {
        const data = await this.api.get(
          `${this.apiBase}/admin/integration/handshake-status?key_id=${this.keyId}&since=${encodeURIComponent(this.sinceIso)}`,
        );
        if (data.handshake && !this.handshake) this.toast('Handshake received from your product', 'success');
        this.handshake = !!data.handshake;
        this.heartbeat = !!data.heartbeat;
        // Once both are in (or handshake for one-off products), stop polling.
        const complete = this.handshake && (!this.product?.requires_activation || this.heartbeat);
        if (complete) this.stopPolling();
      } catch (e) {
        // Poll errors are non-fatal — keep trying.
      }
    },

    // ── Step 4: webhooks ──
    async createWebhook() {
      this.webhook.error = '';
      const url = String(this.webhook.url || '').trim();
      if (!/^https?:\/\//i.test(url)) { this.webhook.error = 'Enter a valid http(s) endpoint URL.'; return; }
      this.webhook.creating = true;
      try {
        const events = String(this.webhook.events || '').split(',').map((e) => e.trim()).filter(Boolean);
        const body = { url, events };
        if (this.product) body.product_id = this.product.id;
        const data = await this.api.post(`${this.apiBase}/admin/webhooks`, body);
        this.webhook.endpoint = data.endpoint;
        this.webhook.secret = data.signing_secret;
        this.webhook.skipped = false;
        this.toast('Webhook endpoint created · secret shown once', 'success');
      } catch (e) {
        this.webhook.error = this.serverMsg(e) || 'Could not create the endpoint.';
      } finally {
        this.webhook.creating = false;
      }
    },
    async testFireWebhook() {
      if (!this.webhook.endpoint) return;
      this.webhook.testing = true;
      try {
        const data = await this.api.post(`${this.apiBase}/admin/webhooks/${this.webhook.endpoint.id}/test-fire`);
        this.webhook.test = data.delivery;
        if (data.delivery.ok) this.toast(`Delivered · ${data.delivery.status_code} · ${data.delivery.latency_ms} ms`, 'success');
        else this.toast(`Delivery failed · status ${data.delivery.status_code}`, 'error');
      } catch (e) {
        this.toast(this.serverMsg(e) || 'Test fire failed', 'error');
      } finally {
        this.webhook.testing = false;
      }
    },

    // ── Step 5: gateways ──
    async validateGateway(key) {
      this.gateways[key].busy = true;
      try {
        const data = await this.api.post(`${this.apiBase}/admin/integration/gateways/${key}/validate`);
        this.gateways[key].status = data.status;
        this.gateways[key].reason = data.reason;
        if (data.kickback_note) this.gateways[key].kickback_note = data.kickback_note;
        this.gatewaysSkipped = false;
        const label = this.gatewayList.find((g) => g.key === key)?.label || key;
        this.toast(`${label}: ${this.gwStatusLabel(data.status)}`, data.status === 'connected' ? 'success' : 'error');
      } catch (e) {
        // The endpoint never 500s, but guard the transport anyway.
        this.gateways[key].status = 'failed';
        this.gateways[key].reason = this.serverMsg(e) || 'Validation call failed.';
      } finally {
        this.gateways[key].busy = false;
      }
    },
    gwBadgeClass(status) {
      return { connected: 'h-badge--success', failed: 'h-badge--danger', unconfigured: 'h-badge--warning' }[status] || 'h-badge--neutral';
    },
    gwDotColor(status) {
      return { connected: 'var(--h-success)', failed: 'var(--h-danger)', unconfigured: 'var(--h-warning)' }[status] || 'var(--h-text-tertiary)';
    },
    gwStatusLabel(status) {
      return { connected: 'connected', failed: 'failed', unconfigured: 'unconfigured' }[status] || 'not checked';
    },

    // ── Utils ──
    slugify(raw) {
      return String(raw || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    },
    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    },
    async copyText(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(text);
        if (text === this.curlSnippet) { this.copiedSnippet = true; setTimeout(() => { this.copiedSnippet = false; }, 1600); }
        else { this.copiedWebhook = true; setTimeout(() => { this.copiedWebhook = false; }, 1600); }
      } catch (e) { /* non-fatal */ }
    },
    serverMsg(e) {
      return e?.response?.data?.message || null;
    },
    toast(text, kind = 'success') {
      const id = ++this.toastSeq;
      this.toasts.push({ id, text, kind });
      setTimeout(() => { this.toasts = this.toasts.filter((t) => t.id !== id); }, 3600);
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.h-iw { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-iw__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-iw__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-iw__content { padding: 26px 32px 64px; max-width: 1180px; width: 100%; }
.h-mono { font-family: var(--h-font-mono, monospace); }
.h-muted { color: var(--h-text-tertiary, #94A3B8); }

.h-iw__head { margin-bottom: 22px; }
.h-iw__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-iw__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; max-width: 70ch; }

.h-iw__grid { display: grid; grid-template-columns: 300px 1fr; gap: 20px; align-items: start; margin-top: 26px; }

/* Card */
.h-card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-card__head { display: flex; align-items: center; justify-content: space-between; padding: 15px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-card__head h3 { margin: 0; font-size: 15px; font-weight: 600; }

/* Badges */
.h-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; border: 1px solid transparent; white-space: nowrap; }
.h-dot { width: 6px; height: 6px; border-radius: 50%; }
.h-badge--success { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-color: var(--h-success-border, #A7F3D0); }
.h-badge--warning { background: var(--h-warning-bg, #FFFBEB); color: var(--h-warning-text, #B45309); border-color: var(--h-warning-border, #FDE68A); }
.h-badge--danger { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }
.h-badge--info { background: var(--h-info-bg, #E0F2FE); color: var(--h-info-text, #0369A1); border-color: var(--h-info-border, #BAE6FD); }
.h-badge--neutral { background: var(--h-neutral-bg, #F1F5F9); color: var(--h-neutral-text, #475569); border-color: var(--h-neutral-border, #E2E8F0); }

/* Left checklist */
.h-vsteps { padding: 8px 18px; }
.h-vstep { display: flex; gap: 13px; padding: 13px 0; position: relative; }
.h-vstep:not(:last-child)::after { content: ''; position: absolute; left: 14px; top: 32px; bottom: -13px; width: 2px; background: var(--h-border, #E2E8F0); }
.h-vstep.is-done:not(:last-child)::after { background: var(--h-accent, #0EA5E9); }
.h-vnode { width: 30px; height: 30px; border-radius: 50%; flex: none; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 12.5px; z-index: 1; border: 2px solid var(--h-border-strong, #CBD5E1); background: var(--h-bg-card, #fff); color: var(--h-text-tertiary, #94A3B8); }
.h-vstep.is-done .h-vnode { background: var(--h-accent, #0EA5E9); border-color: var(--h-accent, #0EA5E9); color: #fff; }
.h-vstep.is-active .h-vnode { border-color: var(--h-accent, #0EA5E9); color: var(--h-accent-dark, #0369A1); background: var(--h-accent-light, #E0F2FE); }
.h-vnode svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; }
.h-vstep__t { font-size: 13.5px; font-weight: 600; }
.h-vstep__d { font-size: 12px; color: var(--h-text-secondary, #64748B); margin-top: 2px; }
.h-vstep__d :deep(.mono) { font-family: var(--h-font-mono, monospace); color: var(--h-text-tertiary, #94A3B8); }
.h-vstep.is-pending .h-vstep__t { color: var(--h-text-tertiary, #94A3B8); }

/* Right active pane */
.h-wcard__head { padding: 20px 24px 6px; }
.h-wcard__head h3 { margin: 0 0 4px; font-size: 17px; font-weight: 600; }
.h-wcard__head p { margin: 0; font-size: 13.5px; color: var(--h-text-secondary, #64748B); }
.h-wcard__body { padding: 16px 24px 8px; }
.h-wcard__foot { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-top: 1px solid var(--h-border, #E2E8F0); background: var(--h-bg-subtle, #F1F5F9); }
.h-wcard__foot-btns { display: flex; gap: 10px; }
.h-wprogress { font-size: 12.5px; color: var(--h-text-secondary, #64748B); }
.h-steplede { font-size: 13px; color: var(--h-text-secondary, #64748B); margin: 0 0 14px; }

/* Buttons */
.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 16px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; line-height: 1; white-space: nowrap; color: var(--h-text-primary); }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--secondary:hover:not(:disabled) { background: var(--h-bg-subtle, #F1F5F9); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary); }
.h-btn--sm { font-size: 12.5px; padding: 6px 12px; }

/* Form fields (native + FormKit genesis inherits tokens) */
.h-field { margin-bottom: 16px; }
.h-flabel { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.h-fhint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; }
.h-input { width: 100%; font: inherit; font-size: 14px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); padding: 9px 12px; background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); box-sizing: border-box; }
.h-input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-formerror { margin: 12px 0 0; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); font-size: 12.5px; color: var(--h-danger-text, #B91C1C); }
.h-note { font-size: 13px; color: var(--h-text-secondary, #64748B); padding: 12px 0; }
.h-kv { display: flex; gap: 10px; align-items: baseline; margin-top: 16px; font-size: 13px; }
.h-kv__k { color: var(--h-text-tertiary, #94A3B8); text-transform: uppercase; letter-spacing: 0.04em; font-size: 11px; font-weight: 600; min-width: 64px; }
.h-kv__v { font-weight: 500; }

/* Code block */
.h-codeblock { background: #0F172A; color: #E2E8F0; border-radius: var(--h-radius, 6px); padding: 13px 15px; font-family: var(--h-font-mono, monospace); font-size: 12.5px; line-height: 1.7; overflow: auto; position: relative; }
.h-codeblock pre { margin: 0; white-space: pre-wrap; word-break: break-all; font-family: inherit; }
.h-codeblock :deep(.k) { color: #7DD3FC; }
.h-codeblock :deep(.s) { color: #C4A882; }
.h-codeblock :deep(.c) { color: #64748B; }
.h-copybtn { position: absolute; top: 10px; right: 10px; font: inherit; font-size: 11px; color: #CBD5E1; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15); border-radius: 5px; padding: 4px 9px; cursor: pointer; }

/* Live check / waiting */
.h-livecheck { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border: 1px solid var(--h-success-border, #A7F3D0); background: var(--h-success-bg, #ECFDF5); border-radius: var(--h-radius, 6px); margin-top: 12px; }
.h-lc-ic { width: 26px; height: 26px; border-radius: 50%; background: var(--h-success, #10B981); color: #fff; display: flex; align-items: center; justify-content: center; flex: none; }
.h-lc-ic svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 3; }
.h-lc-t { font-size: 13px; font-weight: 600; color: var(--h-success-text, #047857); }
.h-lc-d { font-size: 12px; color: var(--h-success-text, #047857); opacity: 0.85; }
.h-waiting { display: flex; align-items: center; gap: 10px; margin-top: 12px; padding: 12px 14px; border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); }
.h-waiting--ok { border-color: var(--h-success-border, #A7F3D0); background: var(--h-success-bg, #ECFDF5); }
.h-waiting__t { font-size: 13px; font-weight: 600; }
.h-waiting__d { font-size: 12px; color: var(--h-text-secondary, #64748B); }
.h-spin { width: 15px; height: 15px; border: 2px solid rgba(14,165,233,.25); border-top-color: var(--h-accent, #0EA5E9); border-radius: 50%; animation: h-iw-sp 0.7s linear infinite; flex: none; }
@keyframes h-iw-sp { to { transform: rotate(360deg); } }

/* Gateways */
.h-gwsec { border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); margin-bottom: 14px; overflow: hidden; }
.h-gwsec__head { display: flex; align-items: center; gap: 11px; padding: 13px 16px; background: var(--h-bg-subtle, #F1F5F9); border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-gwsec__logo { width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 12px; flex: none; }
.h-gwsec__name { font-size: 14px; font-weight: 600; }
.h-gwsec__meta { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); }
.h-gwsec__body { padding: 14px 16px; }
.h-tc-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--h-border, #E2E8F0); }
.h-tc-result { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--h-success-text, #047857); font-weight: 500; }
.h-tc-result svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2.4; }
.h-tc-fail { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); font-weight: 500; }
.h-badge-row { display: flex; align-items: center; gap: 10px; }

/* Health grid */
.h-health__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 14px; }
.h-hcell { border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 14px; }
.h-hl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--h-text-tertiary, #94A3B8); font-weight: 600; }
.h-hv { display: flex; align-items: center; gap: 7px; margin-top: 8px; font-size: 14px; font-weight: 600; }
.h-hv svg { width: 15px; height: 15px; stroke: var(--h-success, #10B981); fill: none; stroke-width: 2.6; }
.h-hv--warn { color: var(--h-warning-text, #B45309); }
.h-hv--warn .h-warnic { stroke: var(--h-warning, #F59E0B); }
.h-hs { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); margin-top: 3px; font-family: var(--h-font-mono, monospace); }

.h-golive-warn { display: flex; gap: 10px; margin-top: 18px; padding: 12px 14px; background: var(--h-warning-bg, #FFFBEB); border: 1px solid var(--h-warning-border, #FDE68A); border-radius: var(--h-radius, 6px); color: var(--h-warning-text, #B45309); }
.h-golive-warn svg { width: 17px; height: 17px; flex: none; stroke: currentColor; fill: none; stroke-width: 2; margin-top: 1px; }
.h-golive-warn__t { font-size: 13px; font-weight: 600; }
.h-golive-warn ul { margin: 6px 0 0; padding-left: 18px; font-size: 12.5px; }
.h-golive-warn li { margin-bottom: 3px; }
.h-golive-ok { display: flex; align-items: center; gap: 10px; margin-top: 18px; padding: 12px 14px; background: var(--h-success-bg, #ECFDF5); border: 1px solid var(--h-success-border, #A7F3D0); border-radius: var(--h-radius, 6px); color: var(--h-success-text, #047857); font-size: 13px; font-weight: 500; }
.h-golive-ok svg { width: 17px; height: 17px; flex: none; stroke: currentColor; fill: none; stroke-width: 2.6; }

/* Toasts */
.h-toasts { position: fixed; bottom: 24px; right: 24px; z-index: 1400; display: flex; flex-direction: column; gap: 10px; }
.h-toast { padding: 11px 16px; border-radius: var(--h-radius, 6px); font-size: 13px; font-weight: 500; box-shadow: var(--h-shadow-panel); border: 1px solid transparent; }
.h-toast--success { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-color: var(--h-success-border, #A7F3D0); }
.h-toast--error { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }
</style>
